import { Spinner } from '@common/components';
import { analytics } from '@common/features/analytics';
import {
  getSpaSettingsPrefetch,
  getSpaTreatmentPrefetch,
  useGetSpaTreatment,
} from '@common/features/spa/hooks';
import { PagePropsWithSSR } from '@common/features/state';
import SpaTreatment from '@routes/SpaTreatment';
import { yourJourneySelectors } from '@store/yourJourney';
import { QueryClient, dehydrate } from '@tanstack/react-query';
import { parseDateToString } from '@utils/helpers';
import moment from 'moment';
import { NextPage, NextPageContext } from 'next';
import NextSeo from 'next-seo';
import Error from 'next/error';
import Head from 'next/head';
import { NextRouter, withRouter } from 'next/router';
import { useEffect } from 'react';
import { Property } from 'shared-types/Property';
import { SpaEntityType } from 'shared-types/Spa';

type SpaTreatmentPageProps = PagePropsWithSSR<{
  id: string;
  property: Property;
  language: string;
  spaTreatment: SpaEntityType;
  url: string;
  socialFallbackImage: { url?: string };
  router: NextRouter;
  spaDate: string;
}>;

const SpaTreatmentPage: NextPage<SpaTreatmentPageProps> = ({
  id,
  property,
  language,
  socialFallbackImage = { url: '' },
  url,
  router,
  spaDate,
}) => {
  const { data: spaTreatment, isFetching } = useGetSpaTreatment(property.id, language, id, spaDate);

  const pageTitle = property.propertyTitle
    ? `${spaTreatment?.name} | ${property.propertyTitle}`
    : spaTreatment?.name;
  const { bookingStep } = router.query;

  useEffect(() => {
    spaTreatment &&
      analytics.track({
        event: 'view_item',
        params: {
          currency: spaTreatment.price.currencyCode,
          value: spaTreatment.price.amount,
          items: [
            {
              item_category: 'spa-treatments',
              item_id: `${spaTreatment.id}`,
              item_name: spaTreatment.name,
              check_in: moment(spaDate).format('YYYY-MM-DD'),
            },
          ],
        },
      });
  }, [spaTreatment]);

  if (isFetching) {
    return <Spinner isActive={isFetching} />;
  }
  if (!spaTreatment) {
    return <Error statusCode={404} />;
  }

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
      </Head>
      <NextSeo
        config={{
          openGraph: {
            title: pageTitle,
            description: spaTreatment?.summaryDescription,
            locale: language,
            url,
            images: [
              {
                url:
                  spaTreatment?.imageGallery && spaTreatment?.imageGallery.length
                    ? spaTreatment?.imageGallery[0].url
                    : socialFallbackImage && socialFallbackImage.url,
              },
            ],
          },
        }}
      />
      <SpaTreatment
        id={id}
        key={id}
        property={property}
        language={language}
        bookingStep={bookingStep}
      />
    </>
  );
};

type SpaTreatmentInitialProps = {
  property: Property;
  language: string;
  url: string;
  store: any;
};

SpaTreatmentPage.getInitialProps = async ({
  property,
  language,
  query,
  url,
  store,
  res,
}: NextPageContext & SpaTreatmentInitialProps): Promise<SpaTreatmentPageProps> => {
  const client = new QueryClient();
  const { spaDate } = yourJourneySelectors.getParsedJourneyParams(store.getState());

  const isServer = !!res;
  await Promise.all([
    ...(isServer
      ? [
          getSpaSettingsPrefetch(client, property.id),
          getSpaTreatmentPrefetch(
            client,
            property.id,
            language,
            query.id,
            parseDateToString(spaDate)
          ),
        ]
      : []),
  ]);

  return {
    id: query.id as string,
    property,
    language,
    url,
    spaDate: parseDateToString(spaDate),
    socialFallbackImage: property.socialFallbackImage,
    dehydratedState: dehydrate(client),
  } as any;
};

export default withRouter(SpaTreatmentPage as any);
